/**
 * File: cipc.ts
 * Path: packages/config/src/accounting/cipc.ts
 * Description: The company's CIPC side in plain words: its registration
 * details, the annual return fee (by turnover, on time or late) and the
 * beneficial ownership declaration, kept as dated versions so each annual
 * return has the declaration that was current when it was filed.
 *
 * Stored under the company books: `accounting/company/cipc/details` and
 * `accounting/company/beneficialOwnership/{versionId}` (versions are never
 * changed; a new one is saved instead). Fees read from CIPC guidance on
 * 2026-10-08 (Companies Act 2008 annual return fees); check them yearly.
 * The due date rule lives in taxCalendar.ts (annualReturnDue).
 */

import type { BooksProfile, Journal, LedgerAccount } from "./accountingTypes";
import { financialYearEnd, financialYearStart } from "./ledger";
import { formatCents } from "./money";
import { profitAndLoss } from "./plainBooks";
import { annualReturnDue } from "./taxCalendar";

/* ---------------------------------------------------------------------------
 * Company details
 * ------------------------------------------------------------------------ */

export const CIPC_COLLECTION = "cipc";
export const CIPC_DETAILS_DOC = "details";

export interface CompanyDetails {
  registeredName: string;
  registrationNumber: string;
  /** YYYY-MM-DD, from the registration certificate (CoR 14.3). */
  incorporatedOn: string | null;
  registeredAddress: string;
  /** One director per line. */
  directors: string[];
  /** SARS income tax reference. */
  taxNumber: string;
  /** How many ordinary shares the company has issued, for the securities register; null until entered. */
  sharesIssued: number | null;
  /** How many beneficial ownership versions exist; the next is this plus one. */
  boVersionCount: number;
  updatedAt: number | null;
  updatedBy: string;
}

const text = (v: unknown, max = 300) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const isoDay = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);

export const emptyCompanyDetails = (defaults: Partial<CompanyDetails> = {}): CompanyDetails => ({
  registeredName: "",
  registrationNumber: "",
  incorporatedOn: null,
  registeredAddress: "",
  directors: [],
  taxNumber: "",
  sharesIssued: null,
  boVersionCount: 0,
  updatedAt: null,
  updatedBy: "",
  ...defaults,
});

export const toCompanyDetails = (data: Record<string, unknown> | undefined | null): CompanyDetails => {
  const r = data ?? {};
  return {
    registeredName: text(r.registeredName, 160),
    registrationNumber: text(r.registrationNumber, 40),
    incorporatedOn: isoDay(r.incorporatedOn),
    registeredAddress: text(r.registeredAddress, 300),
    directors: Array.isArray(r.directors) ? r.directors.map((d) => text(d, 120)).filter(Boolean).slice(0, 20) : [],
    taxNumber: text(r.taxNumber, 20),
    sharesIssued: typeof r.sharesIssued === "number" && Number.isSafeInteger(r.sharesIssued) && r.sharesIssued > 0 ? r.sharesIssued : null,
    boVersionCount: typeof r.boVersionCount === "number" && r.boVersionCount >= 0 ? Math.floor(r.boVersionCount) : 0,
    updatedAt: typeof r.updatedAt === "number" ? r.updatedAt : null,
    updatedBy: text(r.updatedBy, 200),
  };
};

/* ---------------------------------------------------------------------------
 * Annual return fee
 * ------------------------------------------------------------------------ */

export interface AnnualReturnBand {
  /** Turnover from this (in cents) up to the next band. */
  fromCents: number;
  label: string;
  onTimeCents: number;
  lateCents: number;
}

export const ANNUAL_RETURN_BANDS: readonly AnnualReturnBand[] = [
  { fromCents: 0, label: "Under R1 million", onTimeCents: 10_000, lateCents: 15_000 },
  { fromCents: 100_000_000, label: "R1 million to under R10 million", onTimeCents: 45_000, lateCents: 60_000 },
  { fromCents: 1_000_000_000, label: "R10 million to under R25 million", onTimeCents: 200_000, lateCents: 250_000 },
  { fromCents: 2_500_000_000, label: "R25 million or more", onTimeCents: 300_000, lateCents: 400_000 },
];

/** The fee band for a year's turnover (the latest approved financial statements). */
export const annualReturnBand = (turnoverCents: number): AnnualReturnBand => {
  let band = ANNUAL_RETURN_BANDS[0];
  for (const b of ANNUAL_RETURN_BANDS) if (turnoverCents >= b.fromCents) band = b;
  return band;
};

/* ---------------------------------------------------------------------------
 * Beneficial ownership
 * ------------------------------------------------------------------------ */

export const BO_COLLECTION = "beneficialOwnership";

/** The supporting documents CIPC takes with a beneficial ownership filing, each made as a PDF from a declaration. */
export type BoDocument = "mandate" | "securities" | "interests" | "disclosure";

export const BO_DOCUMENTS: readonly { document: BoDocument; label: string }[] = [
  { document: "mandate", label: "Mandate" },
  { document: "securities", label: "Securities register" },
  { document: "interests", label: "Beneficial interest register" },
  { document: "disclosure", label: "Disclosure form" },
];

/**
 * What the books say was paid in for the shares by `asAt`: the credit
 * balance of the Share capital account(s). The securities register shows it
 * beside the shares, so the register and the balance sheet tell one story.
 */
export const shareCapitalCents = (journals: readonly Journal[], accounts: readonly LedgerAccount[], asAt: string): number => {
  const ids = new Set(accounts.filter((a) => a.type === "equity" && (a.taxLine === "Share capital" || a.code === "3000")).map((a) => a.id));
  let cents = 0;
  for (const j of journals) {
    if (j.date > asAt) continue;
    for (const l of j.lines) if (ids.has(l.accountId)) cents += l.creditCents - l.debitCents;
  }
  return cents;
};

/** CIPC wants everyone who holds or controls 5% or more. */
export const BO_THRESHOLD_PERCENT = 5;

export type BoInterest = "direct" | "indirect";
export type BoReason = "first" | "annualReturn" | "change";

export const BO_REASON_LABELS: Readonly<Record<BoReason, string>> = {
  first: "First declaration",
  annualReturn: "Confirmed for the annual return",
  change: "Something changed",
};

export interface BeneficialOwner {
  fullName: string;
  idNumber: string;
  dateOfBirth: string | null;
  nationality: string;
  residentialAddress: string;
  email: string;
  phone: string;
  /** Share of the company held or controlled, 0 to 100, up to 2 decimals. */
  percent: number;
  interest: BoInterest;
  /** For an indirect interest: through whom or what (a trust, another company). */
  via: string;
  /** YYYY-MM-DD they became a beneficial owner. */
  since: string | null;
}

export interface BoDeclaration {
  id: string;
  version: number;
  declaredOn: string;
  reason: BoReason;
  owners: BeneficialOwner[];
  declarantName: string;
  declarantCapacity: string;
  notes: string;
  savedAt: number | null;
  savedBy: string;
}

export const blankOwner = (): BeneficialOwner => ({
  fullName: "",
  idNumber: "",
  dateOfBirth: null,
  nationality: "South African",
  residentialAddress: "",
  email: "",
  phone: "",
  percent: 100,
  interest: "direct",
  via: "",
  since: null,
});

export const boVersionId = (version: number): string => `v-${String(version).padStart(4, "0")}`;

const toOwner = (v: unknown): BeneficialOwner | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  const percent = typeof r.percent === "number" && Number.isFinite(r.percent) ? r.percent : 0;
  return {
    fullName: text(r.fullName, 160),
    idNumber: text(r.idNumber, 40),
    dateOfBirth: isoDay(r.dateOfBirth),
    nationality: text(r.nationality, 60),
    residentialAddress: text(r.residentialAddress, 300),
    email: text(r.email, 200),
    phone: text(r.phone, 40),
    percent: Math.min(100, Math.max(0, percent)),
    interest: r.interest === "indirect" ? "indirect" : "direct",
    via: text(r.via, 200),
    since: isoDay(r.since),
  };
};

export const toBoDeclaration = (id: string, data: Record<string, unknown> | undefined | null): BoDeclaration | null => {
  const r = data ?? {};
  const declaredOn = isoDay(r.declaredOn);
  if (!declaredOn || typeof r.version !== "number") return null;
  return {
    id,
    version: r.version,
    declaredOn,
    reason: r.reason === "first" || r.reason === "change" ? r.reason : "annualReturn",
    owners: Array.isArray(r.owners) ? r.owners.flatMap((o) => toOwner(o) ?? []) : [],
    declarantName: text(r.declarantName, 160),
    declarantCapacity: text(r.declarantCapacity, 80),
    notes: text(r.notes, 1000),
    savedAt: typeof r.savedAt === "number" ? r.savedAt : null,
    savedBy: text(r.savedBy, 200),
  };
};

/** What stops a declaration being saved, in plain words; empty when it's fine. */
export const boIssues = (d: Pick<BoDeclaration, "owners" | "declarantName" | "declaredOn">, today: string): string[] => {
  const issues: string[] = [];
  if (d.owners.length === 0) issues.push("Add at least one owner.");
  d.owners.forEach((o, i) => {
    const who = o.fullName || `Owner ${i + 1}`;
    if (!o.fullName) issues.push(`Owner ${i + 1} needs a full name.`);
    if (!o.idNumber) issues.push(`${who} needs an ID or passport number.`);
    if (!(o.percent > 0)) issues.push(`${who} needs the share they hold.`);
    if (o.interest === "indirect" && !o.via) issues.push(`${who} holds it indirectly: say through whom or what.`);
  });
  const total = d.owners.reduce((s, o) => s + o.percent, 0);
  if (total > 100.0001) issues.push(`The shares add up to ${Math.round(total * 100) / 100}%, more than 100%.`);
  if (!d.declarantName) issues.push("Say who is making the declaration.");
  if (d.declaredOn > today) issues.push("The declaration can't be dated in the future.");
  return issues;
};

/* ---------------------------------------------------------------------------
 * The annual return pack
 * ------------------------------------------------------------------------ */

export interface AnnualReturnPack {
  year: number;
  anniversary: string;
  dueOn: string;
  /** Past the due date and not filed: the late fee applies. */
  late: boolean;
  filedOn: string | null;
  /** The financial year the turnover comes from. */
  turnoverYear: { start: string; end: string; complete: boolean };
  turnoverCents: number;
  band: AnnualReturnBand;
  feeCents: number;
  /**
   * The financial accountability supplement (CoR 30.2): CIPC's questions on
   * how the records are kept, with suggested answers for a company that keeps
   * its own books in this system. It asks no amounts; turnover goes on the
   * annual return itself.
   */
  fas: { question: string; answer: string }[];
  latestBo: Pick<BoDeclaration, "id" | "version" | "declaredOn"> | null;
  checklist: string[];
  warnings: string[];
}

export interface AnnualReturnInput {
  details: CompanyDetails;
  profile: Pick<BooksProfile, "yearEndMonth" | "booksStart">;
  accounts: readonly LedgerAccount[];
  journals: readonly Journal[];
  year: number;
  today: string;
  latestBo: Pick<BoDeclaration, "id" | "version" | "declaredOn"> | null;
  filedOn: string | null;
  dueOnOverride?: string | null;
}

/** Suggested answers to the supplement's questions; the director checks each before filing. */
const supplementAnswers = (details: CompanyDetails): { question: string; answer: string }[] => {
  const who = details.directors.length > 0 ? `${details.directors.join(", ")} (director)` : "The director";
  return [
    { question: "Who records the day-to-day transactions and keeps the financial records?", answer: who },
    { question: "Who compiles the financial information and prepares the financial statements?", answer: who },
    { question: "Who advises the company on keeping its financial records?", answer: "No outside adviser: done in-house" },
    { question: "Who independently reviews or audits the annual financial statements?", answer: "Nobody: they are compiled, not reviewed or audited" },
    { question: "Are the records kept on paper or electronically?", answer: "Electronically" },
    {
      question: "How are the bank reconciliations, balance sheet and income and expense statements prepared?",
      answer: "In the company's accounting system, reconciled to the bank statement every month",
    },
    { question: "How often is stock counted?", answer: "Not applicable: the company holds no stock" },
    { question: "Does the company hold assets in a fiduciary capacity for people who are not related to it?", answer: "No" },
  ];
};

/** Everything needed to file one year's annual return, from the books. Null until the incorporation date is known. */
export const annualReturnPack = (input: AnnualReturnInput): AnnualReturnPack | null => {
  const { details, profile, accounts, journals, year, today } = input;
  if (!details.incorporatedOn) return null;
  const due = annualReturnDue(details.incorporatedOn, year);
  const dueOn = input.dueOnOverride ?? due.dueOn;

  // CIPC uses the latest approved financial statements: the last year that ended before the anniversary.
  const currentEnd = financialYearEnd(due.anniversary, profile.yearEndMonth);
  const dayBefore = new Date(Date.parse(`${financialYearStart(due.anniversary, profile.yearEndMonth)}T00:00:00Z`) - 86_400_000)
    .toISOString()
    .slice(0, 10);
  const previousEnd = financialYearEnd(dayBefore, profile.yearEndMonth);
  // Only a year that has ended and is in the books counts as full; otherwise the books' year so far.
  const complete = previousEnd >= profile.booksStart && previousEnd < today;
  const end = complete ? previousEnd : today < currentEnd ? today : currentEnd;
  const start = financialYearStart(end, profile.yearEndMonth);
  const pl = profitAndLoss(journals, accounts, start, end);
  const band = annualReturnBand(pl.incomeCents);
  const late = !input.filedOn && today > dueOn;

  const warnings: string[] = [];
  if (!complete) {
    warnings.push(
      `The financial year before this return isn't finished or isn't in the books, so the turnover is the books' year so far (${start} to ${end}). Use the figure from the latest approved financial statements when you file.`,
    );
  }
  if (!input.latestBo) warnings.push("There's no beneficial ownership declaration yet. CIPC won't accept the return without one.");
  else if (input.latestBo.declaredOn < `${year - 1}${due.anniversary.slice(4)}`) {
    warnings.push(`The beneficial ownership declaration is from ${input.latestBo.declaredOn}. Confirm it (save a new version) before filing.`);
  }
  if (!details.registrationNumber) warnings.push("Add the registration number under Company details.");

  return {
    year,
    anniversary: due.anniversary,
    dueOn,
    late,
    filedOn: input.filedOn,
    turnoverYear: { start, end, complete },
    turnoverCents: pl.incomeCents,
    band,
    feeCents: late ? band.lateCents : band.onTimeCents,
    fas: supplementAnswers(details),
    latestBo: input.latestBo,
    checklist: [
      "Log in to CIPC eServices (or BizPortal) with the company's customer code.",
      "Check the beneficial ownership declaration is current. If anything changed, save a new version here first, then file it on CIPC (it's free).",
      `Enter the turnover on this page (${formatCents(pl.incomeCents)}) where the annual return asks for it.`,
      "Answer the financial accountability supplement's questions. Suggested answers are on this page: check each one is still true. Nothing is uploaded.",
      `Pay the annual return fee (${late ? "late" : "on time"}, turnover ${band.label.charAt(0).toLowerCase()}${band.label.slice(1)}).`,
      "Submit the annual return and keep the confirmation.",
      "Tick it off with Mark done (here or on the Tax page) so the reminders stop.",
    ],
    warnings,
  };
};
